import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import Plans from './Plans';
import Billing from './Billing';
import PaymentHistory from './PaymentHistory';
import CurrentPlan from './CurrentPlan';
import PageHeader from '../../components/PageHeader';
import { useDispatch, useSelector } from 'react-redux';
import { FREE_PLAN_ID } from '../../utils/common';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { setPlanInfo } from '../../features/auth/authSlice';
import PlanRequests from './PlanRequests';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const PlansAndBilling: React.FC = () => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [selectedTierIndex, setSelectedTierIndex] = useState(0);
  const [isYearly, setIsYearly] = useState(false);
  const [selectedCurrency] = useState<'USD'>('USD'); // Fixed to USD only
  const [isManuallyPlanSelected, setIsManuallyPlanSelected] = useState(false);
  const userDetails = useSelector((state: any) => state.auth.userDetails);

  useEffect(() => {
    fetchOrgPlanInfo();
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handlePlanSelect = (plan: any, tierIndex: number, yearly: boolean, currency: 'USD', convertedPlan: any) => {
    setSelectedPlan(convertedPlan); // Use the plan with USD currency
    setSelectedTierIndex(tierIndex);
    setIsYearly(yearly);
    // selectedCurrency is fixed to 'USD', no need to update
    setIsManuallyPlanSelected(true);
    setValue(2);
  };

  const fetchOrgPlanInfo = async () => {
    if (!userDetails?.organization_id) return;

    try {
      const payload = {
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post<any>('/user/getOrgPlanDetails', payload);
      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decryptedData : any = await decryptResponse(data, getSessionKey() as string);
        const planInfo = decryptedData.plan;
        dispatch(setPlanInfo(planInfo.plan_details));
      }
    } catch (error) {
      console.error('Error fetching peer calling details:', error);
    }
  };

  const getBillingPlan = async (planId: string, tierIndex: number, isYearly: boolean) => {
    type PlanResponse = { status: number;[key: string]: any };
    const payload = {
      data: await encryptResponse({
        plan_id: planId,
      }, REQ_ENC_KEY),
    };
    const planResponse = await httpService.post<PlanResponse>(`${API_BASE_URL}/user/getActiveOrgPlanById`, payload);
    if (planResponse.status === 200 && planResponse.data.data) {
      const data = planResponse.data.data;
      const decryptedData : any = await decryptResponse(data, getSessionKey() as string);
      const planData = decryptedData.plan;
      if (!planData) return;
      setSelectedPlan(planData);
      setIsYearly(isYearly);
      setSelectedTierIndex(tierIndex);
      setIsManuallyPlanSelected(false);
      setValue(2);
    }
  };

  useEffect(() => {
    if (userDetails) {
      if (userDetails?.plan_details?.plan_id === FREE_PLAN_ID) {
        return;
      }
      const activePlan = userDetails.activePlanDetails;
      const isExistingPlanYearly = userDetails.plan_details?.billing_cycle === 'monthly' ? false : true;
      const planTiers = activePlan?.price || [];
      const tierIndex = planTiers.findIndex((tier: any) => {
        return tier.user_tier === userDetails.plan_details?.user_limit;
      });
      if (activePlan && !isManuallyPlanSelected) {
        getBillingPlan(userDetails.plan_details?.plan_id, tierIndex !== -1 ? tierIndex : 0, isExistingPlanYearly);
      }
    }
  }, [isManuallyPlanSelected]);
  const handleUpgradeClick = () => {
    setValue(1); // Switch to Plans tab
  };

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <Box sx={{ mb: 2 }}>
        <PageHeader
          heading="Plans & Billing"
          subheading="Choose the plan that fits your needs and manage your billing information."
          variant="gradient"
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={value} onChange={handleChange} aria-label="plans and billing tabs">
          <Tab label="Current Plan" {...a11yProps(0)} />
          <Tab label="Plans" {...a11yProps(1)} />
          <Tab label="Billing & Usage" {...a11yProps(2)} />
          <Tab label="Payment History" {...a11yProps(3)} />
          <Tab label="Plan Requests" {...a11yProps(4)} />
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        <CurrentPlan onUpgradeClick={handleUpgradeClick} />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Plans
          onPlanSelect={handlePlanSelect}
          selectedTierIndex={selectedTierIndex}
          isYearly={isYearly}
        />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <Billing
          selectedPlan={selectedPlan}
          selectedTierIndex={selectedTierIndex}
          isYearly={isYearly}
          isManuallyPlanSelected={isManuallyPlanSelected}
          setIsManuallyPlanSelected={setIsManuallyPlanSelected}
        />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <PaymentHistory />
      </TabPanel>
      <TabPanel value={value} index={4}>
        <PlanRequests />
      </TabPanel>
    </Box>
  );
};

export default PlansAndBilling;
